import React, { createContext, useContext, useEffect, useMemo } from 'react';

import { IBaseProps } from '@rbi-ctg/frontend';
import {
  IAllFeatureIdsQuery,
  useAllFeatureIdsQuery,
  useFeatureNutritionLazyQuery
} from 'generated/sanity-graphql';
import { usePrevious } from 'hooks/use-previous';

interface IFeatureIds {
  featureAccountId?: string;
  featureAccountDeletePageId?: string;
  featureAccountFormId?: string;
  featureAccountRequestInfoPageId?: string;
  featureDisclaimersId?: string;
  featureFooterId?: string;
  featureGeolocationModalId?: string;
  featureGiftId?: string;
  featureHomePageId?: string;
  featureKioskId?: string;
  featureLayoutId?: string;
  featureMenuId?: string;
  featureNavigationId?: string;
  featureOffersId?: string;
  featureSupportDataId?: string;
  featureStaticMenuId?: string;
  featureCateringPageId?: string;
  featureLoyaltyUIId?: string;
  featureLocationPromoId?: string;
  featureNutritionId?: string;
  featureLoyaltyRewardListCategoriesId?: string;
  // add new feature ids here
}

export interface IFeaturesContext extends IFeatureIds {
  featureIdsLoading: boolean;
}

export const FeaturesContext = createContext<IFeaturesContext>({
  featureIdsLoading: true
});

export const useFeaturesContext = () => useContext(FeaturesContext);

const processAllFeatureIds = (data: IAllFeatureIdsQuery | undefined): IFeatureIds => {
  if (!data) {
    return {};
  }

  const getSingletonId = (id: keyof Omit<IAllFeatureIdsQuery, '__typename'>) => {
    return data?.[id]?.[0]?._id;
  };

  return {
    featureAccountId: getSingletonId('allFeatureAccounts'),
    featureAccountDeletePageId: getSingletonId('allFeatureAccountDeletePages'),
    featureAccountFormId: getSingletonId('allFeatureAccountForms'),
    featureAccountRequestInfoPageId: getSingletonId('allFeatureAccountRequestInfoPages'),
    featureDisclaimersId: getSingletonId('allFeatureDisclaimers'),
    featureFooterId: getSingletonId('allFeatureFooters'),
    featureGeolocationModalId: getSingletonId('allFeatureGeolocationModals'),
    featureGiftId: getSingletonId('allFeatureGifts'),
    featureHomePageId: getSingletonId('allFeatureHomePages'),
    featureLoyaltyUIId: getSingletonId('allLoyaltyUis'),
    featureKioskId: getSingletonId('allFeatureKiosks'),
    featureLayoutId: getSingletonId('allFeatureLayouts'),
    featureMenuId: getSingletonId('allFeatureMenus'),
    featureNavigationId: getSingletonId('allFeatureNavigations'),
    featureNutritionId: getSingletonId('allFeatureNutritions'),
    featureOffersId: getSingletonId('allFeatureOffers'),
    featureSupportDataId: getSingletonId('allSupportData'),
    featureStaticMenuId: getSingletonId('allFeatureStaticMenus'),
    featureCateringPageId: getSingletonId('allFeatureCateringPages'),
    featureLocationPromoId: getSingletonId('allFeatureLocationPromos'),
    featureLoyaltyRewardListCategoriesId: getSingletonId('allRewardLists')
  };
};

export const FeaturesProvider: React.FC<IBaseProps> = ({ children }) => {
  const { data: allFeatureIds, loading: featureIdsLoading } = useAllFeatureIdsQuery();
  const featureIds = useMemo(() => processAllFeatureIds(allFeatureIds), [allFeatureIds]);

  const [dispatchFeatureNutritionQuery] = useFeatureNutritionLazyQuery({
    fetchPolicy: 'network-only'
  });
  const { featureNutritionId } = featureIds;
  const previousFeatureNutritionId = usePrevious(featureNutritionId);

  useEffect(() => {
    // pre-fetch critical data after feature nutrition id changes
    if (previousFeatureNutritionId !== featureNutritionId && featureNutritionId) {
      const prefetch = async () => {
        dispatchFeatureNutritionQuery({
          variables: { featureNutritionId }
        });
      };
      prefetch();
    }
  }, [
    dispatchFeatureNutritionQuery,
    featureIdsLoading,
    featureNutritionId,
    previousFeatureNutritionId
  ]);

  const contextValue = useMemo(() => {
    return { ...featureIds, featureIdsLoading };
  }, [featureIdsLoading, featureIds]);

  return <FeaturesContext.Provider value={contextValue}>{children}</FeaturesContext.Provider>;
};
